import { ICartSession } from "../typings/interfaces/pos/cart-session";
import { CartItem } from "../typings/interfaces/pos/cart-item";
import { PaymentTypeEnum } from "../typings/enum/paymentTypes";
import { ITrayPrintCustomizationLine, ITrayPrintPayment, ITrayPrintRelease } from "../typings/interfaces/print/ticket-print-request";

// Pure formatting helpers shared by every print-request builder (check-in's
// payment-modal.tsx, pickup's PickupReceiptModal.tsx, reprint's
// ticket-print.ts). Deliberately kept dependency-free (no redux action
// creators, no axios) so a component that only needs to format a print
// payload - PickupReceiptModal.tsx included - doesn't have to drag in
// data-fetching/HTTP machinery it never uses just to reach these.

// Shared "first letter of each word capitalized, rest lowercase" convention
// for print/display text sourced from all-caps enums or free-text admin
// entry with no enforced casing (garment color, category/type/material/style
// names, spot/damage/upcharge labels, alteration names) - matches the
// convention cart-row.tsx already uses on-screen for damages
// (`upperFirst(damage.toLowerCase())`), just generalized to multi-word text.
export function toDisplayCase(text: string): string {
  return text
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Garment colors print as the raw enum ("L_BLUE") unless reformatted. The
// enum is always upper snake case, so this always goes through
// `toDisplayCase` after splitting words - there's no "already correctly
// cased" case to preserve, unlike free-text category names.
export function formatGarmentColor(color: string): string {
  return toDisplayCase(color.replace(/_/g, " "));
}

// Each alteration attached to an item, formatted the same way the cart UI
// already shows it (cart-row.tsx: "+ Alterations - {subCategory} {price}"),
// minus the redundant "Alterations - " prefix - the printed section heading
// (see buildCustomizationLines below) already establishes that context.
// Carries the alteration's own `unitPrice` so it prints in the same
// qty/description/price layout as a base item row (previously this charge
// was only silently folded into the item's overall total via
// alterationLinesTotal below, never shown on the alteration's own line).
export function describeAlterationLines(
  item: Pick<CartItem, "alterationItems"> & { redoDetails?: unknown }
): ITrayPrintCustomizationLine[] {
  const isRedo = Boolean(item.redoDetails);
  return (item.alterationItems ?? [])
    .map((a): ITrayPrintCustomizationLine | undefined => {
      const parts = [a.alterationSubCategory?.name, a.alterationPrice?.name]
        .filter((p): p is string => Boolean(p))
        .map(toDisplayCase);
      return parts.length > 0
        ? {
            section: "Alterations",
            label: parts.join(" "),
            price:
              !isRedo && a.unitPrice && a.unitPrice > 0
                ? a.unitPrice
                : undefined,
          }
        : undefined;
    })
    .filter((line): line is ITrayPrintCustomizationLine => Boolean(line));
}

// Garment info (type/material/style) only ever described WHAT the item is,
// never anything the cashier added to it - upcharges, spots, damages,
// release notes (the cart's "Instructions" picklist), and alterations were
// captured in the cart but never made it onto any printed receipt (damages
// still didn't, until this change). Returned as a flat list of
// {section, label, price?} entries - printed grouped under one heading per
// section, each as its own "+"-prefixed line, rather than folded into the
// item's own description string or left unlabeled which kind of line it is.
export function buildCustomizationLines(
  item: Pick<
    CartItem,
    "upcharges" | "spots" | "damages" | "instructions" | "alterationItems"
  > &
    ProcessingPriceFields & { redoDetails?: unknown; specialCharge?: number },
  // Pieces on this receipt row - pickup prints part of a line.
  qty: number = item.qty ?? 1
): ITrayPrintCustomizationLine[] {
  const isRedo = Boolean(item.redoDetails);
  const lines: ITrayPrintCustomizationLine[] = [];

  // Press Only / Clean Only first, right under the struck-through price.
  if (processingBaseUnitPrice(item) !== null) {
    lines.push({
      section: "Processing",
      label: item.pressOnly ? "Press Only" : "Clean Only",
      price: (item.unitPrice ?? 0) * qty,
    });
  }

  // Priced sections print first (Upcharges, Spots, Alterations), then the
  // unpriced ones (Damages, Instructions).
  for (const u of item.upcharges ?? []) {
    const label = u.subUpcharge?.upchargeVariant || u.label;
    if (label) {
      lines.push({
        section: "Upcharges",
        label: toDisplayCase(label),
        price:
          !isRedo && u.subUpcharge?.price && u.subUpcharge.price > 0
            ? u.subUpcharge.price
            : undefined,
      });
    }
  }

  // The spot's name ("Coffee") with its fee; the severity picked (Minor,
  // Medium…) only sets the fee, so it isn't printed.
  for (const s of item.spots ?? []) {
    const label = s.label || s.subSpot?.comment;
    if (label) {
      lines.push({
        section: "Spots",
        label: toDisplayCase(label),
        price:
          !isRedo && s.subSpot?.price && s.subSpot.price > 0
            ? s.subSpot.price
            : undefined,
      });
    }
  }

  lines.push(...describeAlterationLines(item));

  // The Special ticket charge, for just this row's pieces
  const fullQty = item.qty ?? 1;
  if (!isRedo && (item.specialCharge ?? 0) > 0 && fullQty > 0) {
    lines.push({
      section: "Upcharges",
      label: "Special Service",
      price: Math.round(((item.specialCharge ?? 0) * qty * 100) / fullQty) / 100,
    });
  }

  // Damages print on one line, one after the other: "Torn, Faded"
  const damages = (item.damages ?? [])
    .filter((d) => d.damage)
    .map((d) => toDisplayCase(d.damage));
  if (damages.length > 0) lines.push({ section: "Damages", label: damages.join(", ") });

  for (const i of item.instructions ?? []) {
    if (i.instruction) lines.push({ section: "Instructions", label: i.instruction });
  }

  return lines;
}

type ProcessingPriceFields = Partial<
  Pick<
    CartItem,
    "pressOnly" | "cleanOnly" | "baseUnitPrice" | "unitPrice" | "qty" | "isPriceEdited"
  >
> & { redoDetails?: unknown };

// The regular unit price of a Press Only / Clean Only item, when it was
// lowered from it - shown struck through, with the reduced price on the
// Press/Clean Only line. Null for every other item, a hand-edited price, and
// bills saved before the regular price was kept.
export function processingBaseUnitPrice(item: ProcessingPriceFields): number | null {
  if (!(item.pressOnly || item.cleanOnly) || item.redoDetails || item.isPriceEdited) return null;
  const base = item.baseUnitPrice ?? 0;
  return base > (item.unitPrice ?? 0) ? base : null;
}

// strikePrice for a receipt row of `qty` pieces (see ITrayPrintItem).
export function printedStrikePrice(
  item: ProcessingPriceFields,
  qty: number = item.qty ?? 1
): number | undefined {
  const base = processingBaseUnitPrice(item);
  return base === null ? undefined : base * qty;
}

// Sum of what each alteration attached to this item costs - was never added
// into any print builder's item price, so alteration charges silently never
// showed up in a printed Sub Total/Total even though they're billed.
export function alterationLinesTotal(
  item: { alterationItems?: Array<{ unitPrice?: number | null }> }
): number {
  return (item.alterationItems ?? []).reduce(
    (sum, a) => sum + (a.unitPrice ?? 0),
    0
  );
}

// The single, shared definition of "what this item's printed base price is".
// Customizations (upcharges, alterations, priced spots) are printed on their
// own individual lines below the base item row with their respective prices.
// Therefore, the base item row must only display the base garment price,
// not the sum including customizations (which was double-counting them on the receipt).
export function printedBaseItemPrice(
  item: {
    unitPrice?: number;
    qty?: number;
    redoDetails?: unknown;
    alterationOnly?: boolean;
  }
): number {
  if (item.redoDetails || item.alterationOnly) return 0;
  return (item.unitPrice ?? 0) * (item.qty ?? 1);
}

// Backwards-compatible alias for the printed price of an item row.
export const printedItemPrice = printedBaseItemPrice;

// The full pre-discount gross price of an item including all its customizations
// (alterations, upcharges, spots). Used for computing bill-level gross subtotal.
export function grossItemPrice(
  item: {
    unitPrice?: number;
    qty?: number;
    upcharges?: Array<{ subUpcharge?: { price?: number | null } | null }>;
    spots?: Array<{ subSpot?: { price?: number | null } | null }>;
    alterationItems?: Array<{ unitPrice?: number | null }>;
    redoDetails?: unknown;
    alterationOnly?: boolean;
    specialCharge?: number;
  }
): number {
  if (item.redoDetails) return 0;
  const base = item.alterationOnly ? 0 : (item.unitPrice ?? 0) * (item.qty ?? 1);
  return (
    base +
    (item.upcharges ?? []).reduce((sum, u) => sum + (u.subUpcharge?.price || 0), 0) +
    (item.spots ?? []).reduce((sum, s) => sum + (s.subSpot?.price || 0), 0) +
    alterationLinesTotal(item) +
    (item.specialCharge ?? 0)
  );
}

// Every print-request builder composed the printed customer name as just
// `firstName lastName`, silently dropping middleName even though the
// customer record carries one - this is the single place that joins all
// three parts so a receipt's name matches the full name on file.
export function formatFullName(
  person?: {
    firstName?: string | null;
    middleName?: string | null;
    lastName?: string | null;
  } | null
): string {
  return [person?.firstName, person?.middleName, person?.lastName]
    .filter((part) => part && part.trim().length > 0)
    .join(" ")
    .trim();
}

// The pin tag color/tags stored on a cart session (ICartSession.pinTag) never
// carried the 2-letter alias its color config is assigned in Admin > Pin Tag
// Config (e.g. "MO") - only the hex `color` and the bare numeric `tags`
// ("256"), so a printed pin tag could never read "MO256", only "256". Resolves
// the alias by matching the session's stored hex color against the color
// catalog (IColorPintag[]) already used to assign pin tags in the first place.
export function resolvePinTagAlias(
  colorHex: string | null | undefined,
  colorCatalog: Array<{ color: string; alias: string }> | null | undefined
): string | undefined {
  if (!colorHex) return undefined;
  return colorCatalog?.find((c) => c.color === colorHex)?.alias || undefined;
}

// The tray prints the Ready By row as "<date> · After <time>", so the ticket's
// Special / Regular service is appended to the time to show on both copies.
export function readyAfterWithService(time: string, special: boolean): string {
  return `${time} · ${special ? "Special" : "Regular"}`;
}

// Customer type (Admin > customer record) as a single printed letter -
// Retail/Business/Employee, per the client's requested format. Takes the
// first entry of `customerTypePref`, matching the convention already used
// to derive a customer's single effective type elsewhere (Lobby.tsx).
export function customerStatusLetter(
  customerTypePref?: string[] | null
): string | undefined {
  switch ((customerTypePref?.[0] || "").toUpperCase()) {
    case "RETAIL":
      return "R";
    case "BUSINESS":
      return "B";
    case "EMPLOYEE":
      return "E";
    default:
      return undefined;
  }
}

// Customer's on-file "Pay At" billing preference as a single printed letter -
// Monthly Charge/Card on File, per the client's requested format. A customer
// can have both a "Counter"/"Anytime" preference and CARD_ON_FILE/
// MONTHLY_CHARGE in the same `customerPayAtPref` array (see create-customer.tsx);
// only the latter two map to a printed letter. Monthly takes priority if a
// customer somehow has both.
export function paymentStatusLetter(
  customerPayAtPref?: string[] | null
): string | undefined {
  const prefs = customerPayAtPref ?? [];
  if (prefs.includes("MONTHLY_CHARGE")) return "M";
  if (prefs.includes("CARD_ON_FILE")) return "C";
  return undefined;
}

// LATER/MONTHLY both submit the full bill amount as the "payment" so the
// backend can track it as a deferred, NOT_CLEARED charge (see payment.tsx's
// LaterSection/MonthlySection) - nothing was actually collected from the
// customer. Treating that recorded amount as genuinely "paid" on a printed
// receipt made every deferred receipt's balance print as $0, looking fully
// settled when the whole point of the transaction was that it wasn't.
export function isDeferredPayment(paymentType?: string): boolean {
  return (
    paymentType === PaymentTypeEnum.LATER ||
    paymentType === PaymentTypeEnum.MONTHLY
  );
}

// The customer's store credit balance after the most recent Store Credit
// payment in `payments` (the backend records it on each such payment), or
// undefined when there is none - including payments made before the backend
// recorded it, so a reprint of an old bill simply prints no balance line.
// Most recent = highest payment id; lists arrive in either date order.
export function remainingStoreCreditBalance(
  payments: Array<{
    id?: number;
    paymentType: string;
    storeCreditBalanceAfter?: number | null;
  }>
): number | undefined {
  let latest: { id?: number; balance: number } | undefined;
  for (const p of payments) {
    if (p.paymentType !== PaymentTypeEnum.STORE_CREDIT) continue;
    if (p.storeCreditBalanceAfter == null) continue;
    if (!latest || latest.id == null || p.id == null || p.id > latest.id) {
      latest = { id: p.id, balance: p.storeCreditBalanceAfter };
    }
  }
  return latest?.balance;
}

const PRINTED_PAYMENT_METHOD_LABELS: Record<string, string> = {
  [PaymentTypeEnum.CASH]: "Cash",
  [PaymentTypeEnum.CHECK]: "Check",
  [PaymentTypeEnum.CARD]: "Credit Card",
  [PaymentTypeEnum.STORE_CREDIT]: "Store Credit",
};

export interface IPaymentSummary {
  payments: ITrayPrintPayment[];
  // What is still owed after `payments` - never includes change.
  balanceDue: number;
  // Cash handed back (cash tendered beyond what was applied).
  change: number;
}

// Builds the receipt's payment breakdown: one line per payment, in the order
// taken, so two payments by the same method still show separately (deferred
// LATER/MONTHLY records are not payments and are left out). A Cash line
// shows what was actually handed over (its tenderedAmount), and the excess
// over what was applied is the change - so "balance owed" and "change given
// back" are never conflated.
export function buildPaymentSummary(args: {
  payments: Array<{ paymentType: string; amount: number; tenderedAmount?: number | null }>;
  // What is owed for this receipt before payments.
  due: number;
}): IPaymentSummary {
  const payments: ITrayPrintPayment[] = [];
  let appliedTotal = 0;
  let change = 0;
  for (const p of args.payments) {
    if (isDeferredPayment(p.paymentType)) continue;
    const method = PRINTED_PAYMENT_METHOD_LABELS[p.paymentType] ?? toDisplayCase(p.paymentType.replace(/_/g, " "));
    appliedTotal += p.amount;
    const handedOver =
      p.paymentType === PaymentTypeEnum.CASH
        ? Math.max(p.amount, p.tenderedAmount ?? p.amount)
        : p.amount;
    change += handedOver - p.amount;
    payments.push({ method, amount: handedOver });
  }
  return {
    payments,
    balanceDue: Math.max(0, Math.round((args.due - appliedTotal) * 100) / 100),
    change: Math.round(change * 100) / 100,
  };
}

// The bill's releases, each once, with the printed item lines it was tagged
// on - for the "Releases" block after the ticket's summary and the separate
// signature slip. `printedItems` is the request's item list, index-aligned
// with `cartItems`.
export function buildPrintReleases(
  cartItems: Array<Pick<CartItem, "releases">>,
  printedItems: Array<{ qty: number; description: string }>
): ITrayPrintRelease[] {
  const byId = new Map<number, ITrayPrintRelease>();
  cartItems.forEach((item, index) => {
    const printed = printedItems[index];
    for (const release of item.releases ?? []) {
      let entry = byId.get(release.id);
      if (!entry) {
        entry = {
          name: release.name,
          ...(release.agreementText ? { agreementText: release.agreementText } : {}),
          items: [],
        };
        byId.set(release.id, entry);
      }
      if (printed) entry.items.push(`${printed.qty} x ${printed.description}`);
    }
  });
  return Array.from(byId.values());
}

// Request fields for the releases: the summary block on every copy, and the
// signature slip with the store copy only (the store keeps the signed slip)
export function releaseFields(
  releases: ITrayPrintRelease[],
  copyType: "receipt" | "storeCopy"
): { releases?: ITrayPrintRelease[]; releaseSlip?: boolean } {
  if (releases.length === 0) return {};
  return { releases, releaseSlip: copyType === "storeCopy" };
}

// The pin tag a CREATED event's snapshot was checked in with (colour is the
// hex the cart stores), for the "Order Created" card. Later changes show up as
// their own PIN_TAG_UPDATED events.
export function describeSnapshotPinTag(
  originalBillSnapshot: string
): { color: string | null; tags: string[] } | null {
  try {
    const session = JSON.parse(originalBillSnapshot) as ICartSession;
    const tags = (session.pinTag?.tags ?? []).filter(Boolean);
    if (!session.pinTag?.color && tags.length === 0) return null;
    return { color: session.pinTag?.color ?? null, tags };
  } catch {
    return null;
  }
}
